import React, { useCallback, useEffect, useRef, useState } from "react";
import { suggestEmails } from "../services/api";

export default function EmailAutocomplete({ onSelect, exclude = [], placeholder = "Ajouter un destinataire...", inputStyle = {} }) {
    const [value, setValue] = useState("");
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });
    const debounceRef = useRef(null);
    const inputRef = useRef(null);
    const wrapperRef = useRef(null);

    const updateDropdownPos = useCallback(() => {
        if (!inputRef.current) return;
        const rect = inputRef.current.getBoundingClientRect();
        setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 240) });
    }, []);

    const fetchSuggestions = useCallback((q) => {
        clearTimeout(debounceRef.current);
        if (q.trim().length < 2) { setSuggestions([]); setOpen(false); return; }
        debounceRef.current = setTimeout(async () => {
            try {
                const { data } = await suggestEmails(q.trim());
                const filtered = data.filter(s => !exclude.includes(s));
                updateDropdownPos();
                setSuggestions(filtered);
                setOpen(filtered.length > 0);
                setActiveIndex(-1);
            } catch {
                setSuggestions([]);
                setOpen(false);
            }
        }, 200);
    }, [exclude, updateDropdownPos]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const confirmValue = (email) => {
        const trimmed = (email ?? value).trim();
        if (!trimmed || exclude.includes(trimmed)) return;
        onSelect(trimmed);
        setValue("");
        setSuggestions([]);
        setOpen(false);
        setActiveIndex(-1);
        inputRef.current?.focus();
    };

    const handleChange = (e) => {
        setValue(e.target.value);
        setActiveIndex(-1);
        fetchSuggestions(e.target.value);
    };

    const handleKeyDown = (e) => {
        if (open && suggestions.length > 0) {
            if (e.key === "ArrowDown") { e.preventDefault(); setActiveIndex(i => Math.min(i + 1, suggestions.length - 1)); return; }
            if (e.key === "ArrowUp")   { e.preventDefault(); setActiveIndex(i => Math.max(i - 1, -1)); return; }
            if (e.key === "Enter" && activeIndex >= 0) { e.preventDefault(); confirmValue(suggestions[activeIndex]); return; }
            if (e.key === "Escape")    { setOpen(false); setActiveIndex(-1); return; }
        }
        if ((e.key === "Enter" || e.key === "," || e.key === " ") && value.trim()) {
            e.preventDefault();
            confirmValue();
        }
    };

    const baseInputStyle = {
        border: "none",
        outline: "none",
        fontSize: "13px",
        color: "#1e293b",
        background: "transparent",
        padding: "2px 0",
        width: "100%",
        ...inputStyle,
    };

    return (
        <div ref={wrapperRef} style={{ flex: 1, minWidth: "160px", position: "relative" }}>
            <input
                ref={inputRef}
                type="email"
                autoComplete="off"
                aria-label="Adresse email du destinataire"
                value={value}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                onFocus={() => value.trim().length >= 2 && fetchSuggestions(value)}
                placeholder={placeholder}
                style={baseInputStyle}
            />
            {open && (
                <ul style={{
                    position: "fixed",
                    top: dropdownPos.top,
                    left: dropdownPos.left,
                    width: dropdownPos.width,
                    zIndex: 9999,
                    margin: 0,
                    padding: 0,
                    listStyle: "none",
                    background: "white",
                    border: "1px solid #e2e8f0",
                    borderRadius: "7px",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    maxHeight: "220px",
                    overflowY: "auto",
                }}>
                    {suggestions.map((s, i) => (
                        <li key={s} style={{ background: i === activeIndex ? "#eff6ff" : "white" }}>
                            <button
                                type="button"
                                onMouseDown={() => confirmValue(s)}
                                onMouseEnter={() => setActiveIndex(i)}
                                style={{
                                    padding: "8px 12px",
                                    fontSize: "13px",
                                    cursor: "pointer",
                                    color: "#1e293b",
                                    background: "transparent",
                                    border: 0,
                                    borderBottom: i < suggestions.length - 1 ? "1px solid #f1f5f9" : "none",
                                    width: "100%",
                                    textAlign: "left",
                                }}
                            >
                                {s}
                            </button>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
